import React, { useState, useEffect, useContext, useRef } from 'react'
import { useParams } from 'react-router-dom'
import { AuthContext } from '../context/AuthContext'
import { redeem } from './service/studyService'
import { PHASE_PATH } from './StudyPage'
import './study.scss'

const PID_KEY = 'study_participant_id'

// Invite-link entry point: /study/:token, optionally with a part
// (/study/:token/screening, /main, /followup).
//
// This route is deliberately NOT behind ProtectedRoute — redeeming the token is what
// authenticates. It creates the participant's account on first use and re-attaches to
// the same account on every later use, so the probe link still works 6-8 days later on
// a different machine. Participants never see a login form.
export default function StudyEntry ({ mode = 'auto' }) {
  const { token } = useParams()
  const { setCurrentUser } = useContext(AuthContext)
  const [error, setError] = useState('')
  const ranRef = useRef(false)

  useEffect(() => {
    // StrictMode double-invokes effects in dev; redeeming twice is harmless but
    // pointless, and the second run would race the redirect.
    if (ranRef.current) return
    ranRef.current = true

    let cancelled = false
    redeem(token)
      .then(({ user, token: jwt, participantId, phase }) => {
        if (cancelled) return
        // Same storage contract as registerAsGuest, so AuthContext restores this
        // participant through the ordinary /validate path.
        localStorage.setItem('token', jwt)
        localStorage.setItem(PID_KEY, participantId)
        setCurrentUser(user)

        // An explicit part in the link wins (the probe link has to hit /study/followup
        // to trigger the phase flip); otherwise go to whichever part the server says
        // they're in. StudyPage re-checks and corrects either way.
        const dest = mode === 'auto'
          ? (PHASE_PATH[phase] || '/study')
          : (PHASE_PATH[mode] || '/study')

        // Hard navigation, NOT react-router. AuthContext's mount effect is already
        // in flight when this component redeems: it read localStorage before the JWT
        // was written, so it finishes by calling setCurrentUser(null) and clobbers the
        // user we just set — and the route guard then bounces us out. Reloading lets
        // AuthContext initialise once, with the token already in place.
        window.location.replace(dest)
      })
      .catch(e => {
        if (cancelled) return
        setError(e.response?.status === 404
          ? 'unknown_link'
          : (e.response?.data?.error || e.message))
      })
    return () => { cancelled = true }
  }, [token, mode, setCurrentUser])

  return (
    <div className="study-root study-login">
      <div className="study-card">
        {!error && <>
          <h1 className="study-h1">VizPI Study</h1>
          <p>Opening your session…</p>
        </>}
        {error === 'unknown_link' && <>
          <h1 className="study-h1">Link not recognized</h1>
          <p>
            This study link isn’t valid. Please use the exact link from your invitation
            email — it’s personal to you. If you’ve copied it by hand, check for a missing
            or extra character at the end.
          </p>
        </>}
        {error && error !== 'unknown_link' && <>
          <h1 className="study-h1">Couldn’t open your session</h1>
          <p className="study-err">{error}</p>
          <p>Please let the researcher know, and try the link again in a moment.</p>
        </>}
      </div>
    </div>
  )
}
